import React, { useRef, useState, Fragment } from 'react'
import { Printer, Split, X as XIcon } from 'lucide-react'
import api from '../../api/client'
import AlterarComissaoModal from '../../components/modals/AlterarComissaoModal'
import DespesasModal from '../../components/modals/DespesasModal'
import IsentarDefesaModal from '../../components/modals/IsentarDefesaModal'
import AplicarIsencaoModal from '../../components/modals/AplicarIsencaoModal'
import SepararFaturaModal from '../../components/modals/SepararFaturaModal'
import { openPdf, downloadPdf } from '../../utils/pdfUtils'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')

const num = v => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0)
const fmtDate = s => {
  if (!s) return ''
  try { return new Date(s + (s.includes('T') ? '' : 'T12:00:00')).toLocaleDateString('pt-BR') } catch { return s }
}

// ── Cores do tema (azul escuro igual ao exemplo) ──────────────────────────────
const BLUE = '#1a3c6b'

// ── Estilos base (fonte 13, igual fatura do comprador) ───────────────────────
const thBlue = { background: BLUE, color: '#fff', fontWeight: 700, textAlign: 'center', fontSize: 13, padding: '5px 8px', border: `1px solid ${BLUE}` }
const thGray = { background: '#e8e8e8', color: '#000', fontWeight: 700, textAlign: 'left', fontSize: 13, padding: '4px 8px', borderBottom: '1px solid #ccc', borderTop: '1px solid #ccc' }
const td     = { borderBottom: '1px solid #eee', padding: '4px 8px', fontSize: 13, verticalAlign: 'middle' }
const tdC    = { ...td, textAlign: 'center' }
const tdR    = { ...td, textAlign: 'right' }

// Estilos sem bordas verticais — usados só na tabela de lotes vendidos
const thL  = { background: '#e8e8e8', color: '#000', fontWeight: 700, textAlign: 'left', fontSize: 13, padding: '4px 8px', borderBottom: '1px solid #ccc', borderTop: '1px solid #ccc', whiteSpace: 'nowrap' }
const tdL  = { borderBottom: '1px solid #eee', padding: '4px 8px', fontSize: 13, verticalAlign: 'middle' }
const tdLC = { ...tdL, textAlign: 'center', whiteSpace: 'nowrap' }
const tdLR = { ...tdL, textAlign: 'right', whiteSpace: 'nowrap' }

// ── Cabeçalho de seção cinza ──────────────────────────────────────────────────
function SecHdr({ title }) {
  return (
    <div style={{ background: '#f0f0f0', color: '#000', fontWeight: 700, fontSize: 13,
      textAlign: 'center', padding: '5px 10px', marginBottom: 0,
      border: '1px solid #000' }}>
      {title}
    </div>
  )
}

// Texto das crias de um lote (ex: "1 macho(s), 2 fêmea(s)") ou '' se não houver
const criasTxt = (l) => {
  const parts = []
  if (l.qtd_crias_machos) parts.push(`${l.qtd_crias_machos} macho(s)`)
  if (l.qtd_crias_femeas) parts.push(`${l.qtd_crias_femeas} fêmea(s)`)
  return parts.join(', ')
}

// ── Uma página de vendedor ────────────────────────────────────────────────────
function FaturaVendedorPage({ vend, leilao, organizador, showMediasSexo, showMediaGeral, showDadosBancarios, ocultarParcelas, colunasAtivas, modoAnimais }) {
  const lotes    = vend.lotes_venda || []
  const lotesNM  = vend.lotes_nao_movimentados || []
  const lotesDefesa = vend.lotes_defesa || []

  // ── Totais lotes vendidos ──
  const totalAnimais  = lotes.reduce((s, l) => s + (l.qtd || 0), 0)
  const totalVv       = lotes.reduce((s, l) => s + (l.vv || 0), 0)
  const totalCv       = lotes.reduce((s, l) => s + (l.cv_val || 0), 0)
  const totalDesp     = lotes.reduce((s, l) => s + (l.desp || 0), 0)
  const totalDesconto = lotes.reduce((s, l) => s + (l.desconto || 0), 0)
  const totalLiquido  = lotes.reduce((s, l) => s + (l.liquido || 0), 0)

  // ── Totais lotes defesa ──
  const totalDefAnimais = lotesDefesa.reduce((s, l) => s + (l.qtd || 0), 0)
  const totalDefVv = lotesDefesa.reduce((s, l) => s + (l.vv || 0), 0)
  const totalDefCv = lotesDefesa.reduce((s, l) => s + (l.cv_val || 0), 0)
  const totalDespDef = lotesDefesa.reduce((s, l) => s + (l.desp || 0), 0)

  // ── Totais lotes não movimentados ──
  const totalNMAnimais = lotesNM.reduce((s, l) => s + (l.qtd || 0), 0)
  // Despesas de lotes não movimentados (ex: taxa de inscrição por lote) também
  // são cobradas do vendedor independente de o lote ter sido vendido.
  const totalDespNM = lotesNM.reduce((s, l) => s + (l.desp || 0), 0)
  // Despesas totais (venda + defesa + não mov.) — usadas no PAGAMENTO COMISSÃO e TOTAL GERAL
  const totalDespAll = totalDesp + totalDespDef + totalDespNM

  // ── Total geral de animais (vendas + defesas + não movimentados) ──
  const totalAnimaisGeral = totalAnimais + totalDefAnimais + totalNMAnimais

  // ── Médias por sexo ──
  const sexoMap = {}
  lotes.forEach(l => {
    const sx = (l.sexo || 'N/A').toUpperCase()
    if (!sexoMap[sx]) sexoMap[sx] = { sexo: sx, qtd_lotes: 0, qtd_animais: 0, peso_total: 0, valor_total: 0 }
    const e = sexoMap[sx]
    e.qtd_lotes++
    e.qtd_animais += l.qtd || 0
    e.peso_total  += (l.peso || 0) * (l.qtd || 0)
    e.valor_total += l.liquido || 0
  })
  const mediasSexo = Object.values(sexoMap).map(s => ({
    ...s,
    peso_medio:   s.qtd_animais > 0 ? s.peso_total / s.qtd_animais : 0,
    preco_kg:     s.peso_total  > 0 ? s.valor_total / s.peso_total  : 0,
    media_animal: s.qtd_animais > 0 ? s.valor_total / s.qtd_animais : 0,
    media_arroba: s.peso_total  > 0 ? s.valor_total / (s.peso_total / 30) : 0,
  }))

  // ── Média geral ──
  const mediaAnimal = totalAnimais > 0 ? totalLiquido / totalAnimais : 0

  // ── Listagem de pagamentos — agrupa por comprador, depois por data ──
  const compradoresMap = {}
  lotes.forEach(l => {
    const compNome = l.comprador_nome || '–'
    if (!compradoresMap[compNome]) {
      compradoresMap[compNome] = { nome: compNome, parcelas: [], lotes: [] }
    }
    if (!compradoresMap[compNome].lotes.includes(l.numero_lote)) {
      compradoresMap[compNome].lotes.push(l.numero_lote)
    }
    ;(l.parcelas || []).forEach(p => {
      compradoresMap[compNome].parcelas.push({
        data: (p.data || '').split('T')[0],
        prazo: p.prazo,
        valor: p.liquido || 0,
        lote: l.numero_lote,
      })
    })
  })
  // Agrupa parcelas por data dentro de cada comprador
  const compradoresPag = Object.values(compradoresMap).map(c => {
    const grouped = {}
    c.parcelas.forEach(pr => {
      const key = pr.data || ''
      if (!grouped[key]) {
        grouped[key] = { data: pr.data, prazo: pr.prazo, valor: 0, lotes: [] }
      }
      grouped[key].valor += pr.valor
      if (pr.lote && !grouped[key].lotes.includes(pr.lote)) grouped[key].lotes.push(pr.lote)
    })
    const parcelasAgrupadas = Object.values(grouped).filter(p => p.valor !== 0).sort((a, b) => (a.data || '').localeCompare(b.data || ''))
    return { ...c, parcelas: parcelasAgrupadas, total: parcelasAgrupadas.reduce((s, p) => s + p.valor, 0) }
  }).filter(c => c.total > 0)

  const db = vend.dados_bancarios
  const showDb = showDadosBancarios || leilao?.fluxo_comissao === 'separada'
  const logoSrc = organizador.logo_url
    ? (organizador.logo_url.startsWith('http') ? organizador.logo_url
        : `${API_BASE}${organizador.logo_url.startsWith('/') ? '' : '/'}${organizador.logo_url}`)
    : null

  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 12, color: '#000', background: '#fff', padding: '20px 28px 28px' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 4, marginBottom: 6 }}>
        <div style={{ width: 200 }}>
          {logoSrc
            ? <img src={logoSrc} alt="logo" style={{ maxHeight: 90, maxWidth: 200, objectFit: 'contain' }} />
            : <span style={{ fontWeight: 700, fontSize: 14 }}>{organizador.nome}</span>}
        </div>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: BLUE }}>Fatura de Vendedor</div>
          <div style={{ fontSize: 13, marginTop: 3 }}>{leilao.nome} - {fmtDate(leilao.data_leilao)}</div>
          <div style={{ fontSize: 13 }}>{organizador.nome}</div>
          {vend.titulo_fatura && <div style={{ fontSize: 14, fontWeight: 700, color: BLUE, marginTop: 3 }}>{vend.titulo_fatura}</div>}
        </div>
        <div style={{ width: 150, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <img src="/assets/rematei.png" alt="Rematei" style={{ height: 64, objectFit: 'contain' }} />
        </div>
      </div>

      {/* ── Dados do vendedor ── */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, border: `1px solid ${BLUE}` }}>
        <thead>
          <tr><th colSpan={4} style={{ background: '#f0f0f0', color: '#000', fontWeight: 700, fontSize: 17, textAlign: 'center', padding: '5px 10px', border: '1px solid #000' }}>{vend.nome.toUpperCase()}</th></tr>
        </thead>
        <tbody>
          {showDb ? (
            <>
              <tr>
                <td style={{ ...td, fontWeight: 700, width: '20%' }}>CPF/CNPJ: <span style={{ fontWeight: 400 }}>{vend.cpf_cnpj || '–'}</span></td>
                <td style={{ ...td, width: '20%' }}><b>CEP:</b> {vend.cep || '–'}</td>
                <td style={{ ...td, width: '25%' }}><b>Fazenda:</b> {vend.fazenda || '–'}</td>
                <td style={{ ...td, width: '35%', verticalAlign: 'top' }} rowSpan={3}>
                  <b>Dados Bancários:</b><br />
                  {db ? (
                    <>
                      {db.banco   && <div>Banco: {db.banco}</div>}
                      {db.agencia && <div>Agência: {db.agencia} &nbsp; C/C: {db.conta}</div>}
                      {db.pix     && <div>Chave PIX: {db.pix}</div>}
                      {db.is_terceiro && db.titular && <div>Titular: {db.titular}</div>}
                      {db.cpf_cnpj_titular && <div>CPF/CNPJ: {db.cpf_cnpj_titular}</div>}
                    </>
                  ) : '–'}
                </td>
              </tr>
              <tr>
                <td style={td}><b>Município:</b> {[vend.cidade, vend.uf].filter(Boolean).join(' - ') || '–'}</td>
                <td style={td}><b>Telefone:</b> {vend.telefone || '–'}</td>
                <td style={td}><b>I.E.:</b> {vend.fazenda_ie || '–'}</td>
              </tr>
              <tr>
                <td style={td}></td>
                <td style={td}><b>Email:</b> {vend.email || '–'}</td>
                <td style={td}><b>Cidade/UF:</b> {[vend.fazenda_cidade, vend.fazenda_uf].filter(Boolean).join(' - ') || '–'}</td>
              </tr>
            </>
          ) : (
            <>
              <tr>
                <td style={{ ...td, fontWeight: 700, width: '20%' }}>CPF/CNPJ: <span style={{ fontWeight: 400 }}>{vend.cpf_cnpj || '–'}</span></td>
                <td style={{ ...td, width: '20%' }}><b>CEP:</b> {vend.cep || '–'}</td>
                <td style={{ ...td, width: '25%' }}><b>Fazenda:</b> {vend.fazenda || '–'}</td>
                <td style={{ ...td, width: '35%' }}><b>Email:</b> {vend.email || '–'}</td>
              </tr>
              <tr>
                <td style={td}><b>Município:</b> {[vend.cidade, vend.uf].filter(Boolean).join(' - ') || '–'}</td>
                <td style={td}><b>Telefone:</b> {vend.telefone || '–'}</td>
                <td style={td}><b>I.E.:</b> {vend.fazenda_ie || '–'}</td>
                <td style={td}><b>Cidade/UF:</b> {[vend.fazenda_cidade, vend.fazenda_uf].filter(Boolean).join(' - ') || '–'}</td>
              </tr>
            </>
          )}
        </tbody>
      </table>

      {/* ── Lotes Vendidos ── */}
      <SecHdr title="LOTES VENDIDOS" />
      {(() => {
        const cols = colunasAtivas || ['lote', 'qtd', 'sexo', 'raca', 'valor_unitario', 'valor_total', 'comissao_venda', 'total_liquido', 'prazo', 'comprador']
        const activeCols = cols.filter(c => {
          const valid = ['lote','qtd','sexo','raca','animal','tipo','valor_unitario','valor_total','comissao_venda','total_liquido','prazo','comprador']
          return valid.includes(c)
        })
        const temAnimalCol = activeCols.includes('animal')
        const linhasMultilinha = (texto) => (texto || '–').split('<br/>').map((linha, idx) => <div key={idx}>{linha}</div>)
        const colDefs = {
          lote:           { label: 'Lote',          align: 'center', render: l => <td key="lote" style={{ ...tdL, fontWeight: 700 }}>{l.numero_lote}</td> },
          qtd:            { label: 'Qtd',           align: 'center', render: l => <td key="qtd" style={tdLC}>{l.qtd}</td> },
          sexo:           { label: 'Sexo',          align: 'center', render: l => (
            <td key="sexo" style={tdLC}>{temAnimalCol && l.sexo_animais ? linhasMultilinha(l.sexo_animais) : (l.raca_resumo ? '–' : (l.sexo || '').toUpperCase())}</td>
          ) },
          raca:           { label: 'Raça',          align: 'center', render: l => (
            <td key="raca" style={tdLC}>{temAnimalCol ? linhasMultilinha(l.raca_animais) : (l.raca_resumo || (l.raca || '–').toUpperCase())}</td>
          ) },
          animal:         { label: 'Animal',        align: 'left',   render: l => (
            <td key="animal" style={{ ...tdL, whiteSpace: 'nowrap' }}>
              {linhasMultilinha(l.animal_nome)}
            </td>
          ) },
          tipo:           { label: 'Tipo',          align: 'center', render: l => <td key="tipo" style={tdLC}>{l.tipo_animal || '–'}</td> },
          valor_unitario: { label: 'Valor Unit.',   align: 'right',  render: l => <td key="vunit" style={tdLR}>R$ {num(l.vv_unit)}</td> },
          valor_total:    { label: 'Venda (Vlr Total)', align: 'right', render: l => <td key="vtotal" style={tdLR}>R$ {num(l.vv)}</td> },
          comissao_venda: { label: 'Com./Taxa Venda', align: 'center', render: l => <td key="cv" style={tdLC}>{num(l.cv_pct)}% &nbsp; R$ {num(l.cv_val)}</td> },
          total_liquido:  { label: 'Total Líquido', align: 'right',  render: l => <td key="tliq" style={{ ...tdLR, fontWeight: 700 }}>R$ {num(l.liquido)}</td> },
          prazo:          { label: 'Prazo',         align: 'center', render: l => <td key="prazo" style={tdLC}>{fmtDate(l.parcelas?.[0]?.data)}{l.parcelas?.[0]?.prazo ? <div style={{ fontSize: 10, color: '#555' }}>{l.parcelas[0].prazo}</div> : null}</td> },
          comprador:      { label: 'Comprador',     align: 'left',   render: l => <td key="comp" style={{ ...tdL, whiteSpace: 'nowrap' }}>{l.comprador_nome || ''}</td> },
        }

        // Footer totals mapping
        const footerMap = {
          valor_total: <td key="ft_vt" style={{ borderBottom: '1px solid #ccc', padding: '4px 8px', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', textAlign: 'right' }}>R$ {num(totalVv)}</td>,
          comissao_venda: <td key="ft_cv" style={{ borderBottom: '1px solid #ccc', padding: '4px 8px', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', textAlign: 'right' }}>{totalCv > 0 ? <>R$ {num(totalCv)}</> : ''}</td>,
          total_liquido: <td key="ft_tl" style={{ borderBottom: '1px solid #ccc', padding: '4px 8px', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', textAlign: 'right' }}>R$ {num(totalLiquido)}</td>,
          comprador: <td key="ft_comp" style={{ borderBottom: '1px solid #ccc', padding: '4px 8px', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', textAlign: 'right' }}>Lotes &nbsp; {lotes.length}</td>,
        }

        // Determine span for the first part of footer
        const firstTotalIdx = activeCols.findIndex(c => c === 'valor_total' || c === 'comissao_venda' || c === 'total_liquido' || c === 'comprador')
        const spanCount = firstTotalIdx > 0 ? firstTotalIdx : activeCols.length

        return (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
            <thead>
              <tr>
                {activeCols.map(c => (
                  <th key={c} style={{ ...thL, textAlign: colDefs[c].align }}>{colDefs[c].label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lotes.length === 0
                ? <tr><td colSpan={activeCols.length} style={{ ...tdLC, color: '#999' }}>Nenhum lote vendido.</td></tr>
                : lotes.map((l, i) => {
                  const criasParts = []
                  if (l.qtd_crias_machos) criasParts.push(`${l.qtd_crias_machos} macho(s)`)
                  if (l.qtd_crias_femeas) criasParts.push(`${l.qtd_crias_femeas} fêmea(s)`)
                  return (
                    <Fragment key={i}>
                      <tr style={{ background: i % 2 === 0 ? '#fff' : '#f5f5f5' }}>
                        {activeCols.map(c => colDefs[c].render(l))}
                      </tr>
                      {criasParts.length > 0 && (
                        <tr style={{ background: '#fafafa' }}>
                          <td colSpan={activeCols.length} style={{ ...tdL, fontSize: 10, padding: '1px 6px' }}>
                            <strong>Crias:</strong> {criasParts.join(', ')}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })
              }
            </tbody>
            <tfoot>
              <tr style={{ background: '#f0f0f0', color: '#000', fontWeight: 700, fontSize: 13, borderTop: '2px solid #000' }}>
                <td colSpan={spanCount} style={{ borderBottom: '1px solid #ccc', padding: '4px 8px', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>
                  Animais: {totalAnimais}
                  {(() => {
                    const cm = lotes.reduce((s, l) => s + (l.qtd_crias_machos || 0), 0)
                    const cf = lotes.reduce((s, l) => s + (l.qtd_crias_femeas || 0), 0)
                    return cm + cf > 0 ? <>&nbsp;&nbsp;&nbsp;Crias: {cm}M / {cf}F</> : null
                  })()}
                </td>
                {activeCols.slice(spanCount).map(c => footerMap[c] || <td key={`ft_${c}`} style={{ borderBottom: '1px solid #ccc', padding: '4px 8px' }}></td>)}
              </tr>
            </tfoot>
          </table>
        )
      })()}

      {/* ── Lotes Defesa (após lotes vendidos) ── */}
      {lotesDefesa.length > 0 && (<>
      <SecHdr title="LOTES NÃO MOVIMENTADOS / DEFESA" />
      {(() => {
        const isCavaloDef = leilao?.fluxo_comissao === 'cavalo'
        const linhasMultilinhaDef = (texto) => (texto || '–').split('<br/>').map((linha, idx) => <div key={idx}>{linha}</div>)
        return (
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '6%' }} />
          <col style={{ width: '6%' }} />
          <col style={{ width: isCavaloDef ? '13%' : '20%' }} />
          {isCavaloDef && <col style={{ width: '18%' }} />}
          <col style={{ width: '12%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: '12%' }} />
        </colgroup>
        <thead>
          <tr>
            {['Lote','Qtd','Sexo','Raça', ...(isCavaloDef ? ['Animal'] : []),'Comissão Defesa','Valor Unit.','Valor Total'].map(h => (
              <th key={h} style={{ ...thGray, textAlign: h === 'Animal' ? 'left' : 'center' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lotesDefesa.map((l, i) => (
            <Fragment key={i}>
            <tr style={{ background: '#fff8e1' }}>
              <td style={{ ...tdC, fontWeight: 700 }}>{l.numero_lote}</td>
              <td style={tdC}>{l.qtd}</td>
              {isCavaloDef ? (
                <>
                  <td style={tdC}>{linhasMultilinhaDef(l.sexo_animais)}</td>
                  <td style={tdC}>{linhasMultilinhaDef(l.raca_animais)}</td>
                  <td style={{ ...tdL, whiteSpace: 'nowrap' }}>{linhasMultilinhaDef(l.animal_nome)}</td>
                </>
              ) : (
                <>
                  <td style={tdC}>{(l.sexo || '').toUpperCase()}</td>
                  <td style={tdC}>{(l.raca || '–').toUpperCase()}</td>
                </>
              )}
              <td style={tdR}>R$ {num(l.cv_val || 0)}</td>
              <td style={tdR}>R$ {num(l.vv_unit || 0)}</td>
              <td style={tdR}>R$ {num(l.vv || 0)}</td>
            </tr>
            {criasTxt(l) && (
              <tr style={{ background: '#fff8e1' }}>
                <td colSpan={isCavaloDef ? 8 : 7} style={{ ...tdL, fontSize: 10, padding: '1px 6px' }}>
                  <strong>Crias:</strong> {criasTxt(l)}
                </td>
              </tr>
            )}
            </Fragment>
          ))}
        </tbody>
      </table>
        )
      })()}

      {/* Pagamento Comissão Defesa — não exibe separado quando é separada com vendas (já consolidado abaixo) */}
      {totalDefCv > 0 && !(['separada', 'cavalo'].includes(leilao.fluxo_comissao) && totalCv > 0) && (
        <div style={{ border: '1px solid #ccc', borderRadius: 4, padding: '10px 14px', marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Pagamento Comissão Defesa</div>
          {(() => {
            const fluxoLeilao = leilao.fluxo_comissao || ''
            const cobrarDefesa = leilao.gta_cobrar_defesa || false
            let gtaNotaDef = 0
            if (fluxoLeilao === 'gta_nota' && cobrarDefesa) {
              const valorPorAnimal = parseFloat(leilao.valor_por_animal) || 0
              const valorNota = parseFloat(leilao.valor_nota) || 0
              const gtaModo = leilao.gta_modo_cobranca || 'por_vendedor'
              if (valorPorAnimal > 0 || valorNota > 0) {
                const gtaTotal = valorPorAnimal * totalDefAnimais
                const notaTotal = gtaModo === 'por_lote' ? valorNota * lotesDefesa.length : valorNota * 1
                gtaNotaDef = gtaTotal + notaTotal
              }
            }
            const totalPagar = totalDefCv + gtaNotaDef
            const orgDb = organizador.dados_bancarios
            return (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginBottom: 6 }}>
                  <span>Defesas: {lotesDefesa.length} lotes, {totalDefAnimais} animais | Total defesa: R$ {num(totalDefVv)} | Comissão: R$ {num(totalDefCv)}{gtaNotaDef > 0 ? ` | GTA+Nota: R$ ${num(gtaNotaDef)}` : ''}</span>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>Total a pagar: R$ {num(totalPagar)}</span>
                </div>
                {orgDb && (
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#333', borderTop: '1px solid #eee', paddingTop: 4 }}>
                    Dados Bancários: {orgDb.banco && `Banco: ${orgDb.banco}`}{orgDb.agencia && ` | AG: ${orgDb.agencia} C/C: ${orgDb.conta}`}{orgDb.pix && ` | PIX: ${orgDb.pix}`}
                  </div>
                )}
              </>
            )
          })()}
        </div>
      )}
      </>)}

      {/* ── Lotes Não Movimentados (só mostra se tiver lotes) ── */}
      {lotesNM.length > 0 && (<>
      <SecHdr title="LOTES NÃO MOVIMENTADOS" />
      {(() => {
        const showTipo = (colunasAtivas || []).includes('tipo');
        const isCavaloNM = leilao?.fluxo_comissao === 'cavalo'
        const cmNM = lotesNM.reduce((sum, l) => sum + (l.qtd_crias_machos || 0), 0)
        const cfNM = lotesNM.reduce((sum, l) => sum + (l.qtd_crias_femeas || 0), 0)
        const criasTotNM = cmNM + cfNM > 0 ? `Crias: ${cmNM}M / ${cfNM}F` : ''
        const linhasMultilinhaNM = (texto) => (texto || '–').split('<br/>').map((linha, idx) => <div key={idx}>{linha}</div>)
        return (
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '8%' }} />
          <col style={{ width: '8%' }} />
          {showTipo && <col style={{ width: '13%' }} />}
          <col style={{ width: isCavaloNM ? '8%' : '13%' }} />
          <col style={{ width: isCavaloNM ? '16%' : (showTipo ? '16%' : '20%') }} />
          {isCavaloNM ? (
            <col style={{ width: '30%' }} />
          ) : (
            <>
              <col style={{ width: '12%' }} />
              <col style={{ width: '12%' }} />
            </>
          )}
        </colgroup>
        <thead>
          <tr>
            {['Lote','Qtd', ...(showTipo ? ['Tipo'] : []),'Sexo','Raça', ...(isCavaloNM ? ['Animal'] : ['Peso (kg)','Peso (@)'])].map(h => (
              <th key={h} style={{ ...thGray, textAlign: ['Peso (kg)','Peso (@)'].includes(h) ? 'right' : (h === 'Animal' ? 'left' : 'center') }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lotesNM.map((l, i) => (
            <Fragment key={i}>
            <tr style={{ background: i % 2 === 0 ? '#fff' : '#f5f5f5' }}>
              <td style={{ ...tdC, fontWeight: 700 }}>{l.numero_lote}</td>
              <td style={tdC}>{l.qtd}</td>
              {showTipo && <td style={tdC}>{(l.tipo_animal || '–').toUpperCase()}</td>}
              {isCavaloNM ? (
                <>
                  <td style={tdC}>{linhasMultilinhaNM(l.sexo_animais)}</td>
                  <td style={tdC}>{linhasMultilinhaNM(l.raca_animais)}</td>
                  <td style={{ ...tdL, whiteSpace: 'nowrap' }}>{linhasMultilinhaNM(l.animal_nome)}</td>
                </>
              ) : (
                <>
                  <td style={tdC}>{(l.sexo || '').toUpperCase()}</td>
                  <td style={tdC}>{(l.raca || '–').toUpperCase()}</td>
                  <td style={tdR}>{num(l.peso || 0)}</td>
                  <td style={tdR}>{num((l.peso || 0) / 30)}</td>
                </>
              )}
            </tr>
            {criasTxt(l) && (
              <tr style={{ background: i % 2 === 0 ? '#fff' : '#f5f5f5' }}>
                <td colSpan={(showTipo ? 6 : 5) + (isCavaloNM ? 0 : 1)} style={{ ...tdL, fontSize: 10, padding: '1px 6px' }}>
                  <strong>Crias:</strong> {criasTxt(l)}
                </td>
              </tr>
            )}
            </Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: '#f0f0f0', fontWeight: 700, fontSize: 13, borderTop: '2px solid #000' }}>
            <td style={{ padding: '4px 8px', fontWeight: 700 }}>Animais</td>
            <td style={{ padding: '4px 8px', fontWeight: 700, textAlign: 'center' }}>{totalNMAnimais}</td>
            {showTipo && <td></td>}
            <td style={{ padding: '4px 8px', fontWeight: 700 }}>Lotes</td>
            <td style={{ padding: '4px 8px', fontWeight: 700 }}>{lotesNM.length}</td>
            {isCavaloNM ? <td style={{ padding: '4px 8px', fontWeight: 700 }}>{criasTotNM}</td> : <><td style={{ padding: '4px 8px', fontWeight: 700 }}>{criasTotNM}</td><td></td></>}
          </tr>
        </tfoot>
      </table>
        );
      })()}
      </>)}

      {/* ── Listagem dos Pagamentos ── */}
      <SecHdr title="LISTAGEM DOS PAGAMENTOS" />
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, fontSize: 12 }}>
        <thead>
          <tr>
            {['Comprador', 'Valor', 'Data', 'Lote(s)', 'Condição'].map(h => (
              <th key={h} style={thGray}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {compradoresPag.map((c, ci) => {
            const nParcelas = c.parcelas.length
            const GRID_THRESHOLD = 6
            const bg = ci % 2 === 0 ? '#fff' : '#f5f5f5'
            const borderTop = ci > 0 ? '2px solid #ccc' : '1px solid #eee'

            if (nParcelas > GRID_THRESHOLD) {
              // Modo grid compacto para muitas parcelas
              const allLotes = [...new Set(c.parcelas.flatMap(p => p.lotes || []))].sort()
              return (
                <React.Fragment key={ci}>
                  <tr style={{ background: bg }}>
                    <td style={{ ...td, fontWeight: 700, verticalAlign: 'middle', borderTop, background: bg }}>
                      {c.nome}
                      {!ocultarParcelas && (
                        <div style={{ fontSize: 11, fontWeight: 400, color: '#555', marginTop: 2 }}>
                          Parcelas: {nParcelas}
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdC, fontWeight: 700, borderTop }}>R$ {num(c.total)}</td>
                    <td style={{ ...tdC, borderTop }}>{nParcelas} parcelas</td>
                    <td style={{ ...tdC, borderTop }}>{allLotes.join(', ')}</td>
                    <td style={{ ...tdC, borderTop }}>Parcelado</td>
                  </tr>
                  <tr style={{ background: bg }}>
                    <td colSpan={5} style={{ padding: '4px 6px', borderTop: '1px solid #eee' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                        <tbody>
                          {(() => {
                            const ncols = 4
                            const rows = []
                            for (let r = 0; r < Math.ceil(nParcelas / ncols); r++) {
                              const cells = []
                              for (let cc = 0; cc < ncols; cc++) {
                                const idx = r * ncols + cc
                                if (idx < nParcelas) {
                                  const p = c.parcelas[idx]
                                  cells.push(
                                    <td key={cc} style={{ border: '1px solid #ddd', padding: '2px 4px', fontSize: 10 }}>
                                      {String(idx + 1).padStart(2, '0')}/{nParcelas} - {fmtDate(p.data)} - R$ {num(p.valor)}
                                    </td>
                                  )
                                } else {
                                  cells.push(<td key={cc} style={{ border: '1px solid #ddd', padding: '2px 4px' }}></td>)
                                }
                              }
                              rows.push(<tr key={r}>{cells}</tr>)
                            }
                            return rows
                          })()}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </React.Fragment>
              )
            }

            // Modo normal: uma linha por parcela
            return c.parcelas.map((p, pi) => {
              const borderTopNormal = pi === 0 && ci > 0 ? '2px solid #ccc' : '1px solid #eee'
              return (
                <tr key={`c-${ci}-p-${pi}`} style={{ background: bg }}>
                  {pi === 0 && (
                    <td rowSpan={nParcelas} style={{ ...td, fontWeight: 700, verticalAlign: 'middle', borderTop: borderTopNormal, background: bg }}>
                      {c.nome}
                      {!ocultarParcelas && (
                        <div style={{ fontSize: 11, fontWeight: 400, color: '#555', marginTop: 2 }}>
                          Parcelas: {nParcelas}
                        </div>
                      )}
                    </td>
                  )}
                  <td style={{ ...tdC, fontWeight: 700, borderTop: borderTopNormal }}>R$ {num(p.valor)}</td>
                  <td style={{ ...tdC, borderTop: borderTopNormal }}>{fmtDate(p.data)}</td>
                  <td style={{ ...tdC, borderTop: borderTopNormal }}>{(p.lotes || []).join(', ')}</td>
                  <td style={{ ...tdC, borderTop: borderTopNormal }}>{p.prazo || '–'}</td>
                </tr>
              )
            })
          })}
        </tbody>
      </table>

      {/* ── PAGAMENTO COMISSÃO (somente fluxo separada) ── */}
      {['separada', 'cavalo'].includes(leilao.fluxo_comissao) && (totalCv > 0 || totalDefCv > 0) && (
        <>
          <SecHdr title="PAGAMENTO COMISSÃO" />
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
            <thead>
              <tr>
                <th style={thGray}>Organizador</th>
                <th style={thGray}>Dados Bancários</th>
                <th style={{ ...thGray, textAlign: 'center' }}>Comissão</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ ...td, verticalAlign: 'top' }}>
                  <strong>{organizador.nome}</strong>
                  {organizador.cnpj && <><br/>CNPJ: {organizador.cnpj}</>}
                </td>
                <td style={{ ...td, verticalAlign: 'top' }}>
                  {organizador.dados_bancarios ? (
                    <>
                      {organizador.dados_bancarios.banco && <>Banco: {organizador.dados_bancarios.banco}<br/></>}
                      {organizador.dados_bancarios.agencia && <>AG: {organizador.dados_bancarios.agencia}  C/C: {organizador.dados_bancarios.conta || ''}<br/></>}
                      {organizador.dados_bancarios.pix && <>PIX: {organizador.dados_bancarios.pix}</>}
                    </>
                  ) : '–'}
                </td>
                <td style={{ ...td, textAlign: 'center', verticalAlign: 'middle' }}>
                  {totalCv > 0 && <div><strong>Comissão Venda:</strong> R$ {num(totalCv)}</div>}
                  {totalDefCv > 0 && <div><strong>Comissão Defesa:</strong> R$ {num(totalDefCv)}</div>}
                  {totalDespAll > 0 && <div><strong>Despesas:</strong> R$ {num(totalDespAll)}</div>}
                  {(() => {
                    const qtdRemovidas = lotes.filter(l => l.despesas_removidas_manualmente).length
                    return qtdRemovidas > 0 ? (
                      <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>
                        Despesas removidas manualmente em {qtdRemovidas} lote{qtdRemovidas > 1 ? 's' : ''}
                      </div>
                    ) : null
                  })()}
                  <div style={{ marginTop: 4 }}><strong>Total:</strong> R$ {num(totalCv + totalDefCv + totalDespAll)}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {/* ── Média por Sexo ── */}
      {showMediasSexo && (<>
        <SecHdr title="MÉDIA POR SEXO - VENDA (TOTAL - COMISSÃO)" />
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, tableLayout: 'fixed' }}>
          <colgroup>
            {['10%','10%','10%','12%','12%','14%','12%','14%'].map((w,i) => <col key={i} style={{ width: w }} />)}
          </colgroup>
          <thead>
            <tr>
              {['Sexo','Qtd Lotes','Qtd Animais','Peso Médio (kg)','Preço/kg','Média Animal','Média (@)','Valor Total'].map(h => (
                <th key={h} style={{ background: '#e8e8e8', color: '#000', fontWeight: 700, textAlign: 'center', fontSize: 13, padding: '4px 8px', border: '1px solid #ccc' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mediasSexo.length === 0
              ? <tr><td colSpan={8} style={{ textAlign: 'center', color: '#999', border: '1px solid #ccc', padding: '8px' }}>–</td></tr>
              : mediasSexo.map((s, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f5f5f5' }}>
                  {[s.sexo, s.qtd_lotes, s.qtd_animais].map((v, j) => (
                    <td key={j} style={{ border: '1px solid #ccc', padding: '4px 8px', fontSize: 13, textAlign: 'center' }}>{v}</td>
                  ))}
                  <td style={{ border: '1px solid #ccc', padding: '4px 8px', fontSize: 13, textAlign: 'right' }}>{num(s.peso_medio)}</td>
                  <td style={{ border: '1px solid #ccc', padding: '4px 8px', fontSize: 13, textAlign: 'right' }}>R$ {num(s.preco_kg)}</td>
                  <td style={{ border: '1px solid #ccc', padding: '4px 8px', fontSize: 13, textAlign: 'right' }}>R$ {num(s.media_animal)}</td>
                  <td style={{ border: '1px solid #ccc', padding: '4px 8px', fontSize: 13, textAlign: 'right' }}>R$ {num(s.media_arroba)}</td>
                  <td style={{ border: '1px solid #ccc', padding: '4px 8px', fontSize: 13, textAlign: 'right' }}>R$ {num(s.valor_total)}</td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </>)}

      {/* ── Média Geral ── */}
      {showMediaGeral && (<>
        <SecHdr title="MÉDIA GERAL" />
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
          <thead>
            <tr>
              {['Total de Animais','Total de Lotes','Valor Total (líquido)','Média Animal'].map(h => (
                <th key={h} style={thGray}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdC}>{totalAnimais}</td>
              <td style={tdC}>{lotes.length}</td>
              <td style={tdR}>R$ {num(totalLiquido)}</td>
              <td style={tdR}>R$ {num(mediaAnimal)}</td>
            </tr>
          </tbody>
        </table>
      </>)}

      {/* ── Total Geral ── */}
      <SecHdr title="TOTAL GERAL" />
      {(() => {
        const isCavaloTG = leilao.fluxo_comissao === 'cavalo'
        const modoAnim = ['total', 'vendidos', 'ambos'].includes(modoAnimais) ? modoAnimais : (isCavaloTG ? 'ambos' : 'total')
        const mostraVendidos = modoAnim !== 'total'
        const mostraTotal = modoAnim !== 'vendidos'
        const nColsTG = (totalDesconto > 0 ? 4 : 3) + (mostraVendidos ? 1 : 0) + (mostraTotal ? 1 : 0)
        return (
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20, tableLayout: 'fixed' }}>
        <colgroup>
          {Array.from({ length: nColsTG }).map((_, i) => (
            <col key={i} style={{ width: `${100 / nColsTG}%` }} />
          ))}
        </colgroup>
        <tbody>
          <tr>
            {mostraVendidos && (
            <td style={{ border: '1px solid #ccc', padding: '6px 10px', fontSize: 13, textAlign: 'center', background: '#f5f5f5' }}>
              <div style={{ fontWeight: 700, color: '#555', fontSize: 12 }}>Animais Vendidos</div>
              <div style={{ fontWeight: 700, fontSize: 16, marginTop: 2 }}>{totalAnimais}</div>
            </td>
            )}
            {mostraTotal && (
            <td style={{ border: '1px solid #ccc', padding: '6px 10px', fontSize: 13, textAlign: 'center', background: '#f5f5f5' }}>
              <div style={{ fontWeight: 700, color: '#555', fontSize: 12 }}>Total Animais</div>
              <div style={{ fontWeight: 700, fontSize: 16, marginTop: 2 }}>{totalAnimaisGeral}</div>
            </td>
            )}
            <td style={{ border: '1px solid #ccc', padding: '6px 10px', fontSize: 13, textAlign: 'center', background: '#f5f5f5' }}>
              <div style={{ fontWeight: 700, color: '#555', fontSize: 12 }}>Total Venda</div>
              <div style={{ fontWeight: 700, fontSize: 16, marginTop: 2 }}>R$ {num(totalVv)}</div>
            </td>
            <td style={{ border: '1px solid #ccc', padding: '6px 10px', fontSize: 13, textAlign: 'center', background: '#eef3f8' }}>
              <div style={{ fontWeight: 700, color: '#1a3c6b', fontSize: 12 }}>Com./Taxa Geral</div>
              <div style={{ fontWeight: 700, fontSize: 16, marginTop: 2, color: '#1a3c6b' }}>R$ {num(['separada', 'cavalo'].includes(leilao.fluxo_comissao) ? totalCv + totalDefCv + totalDespAll : totalCv)}</div>
            </td>
            {totalDesconto > 0 && (
              <td style={{ border: '1px solid #ccc', padding: '6px 10px', fontSize: 13, textAlign: 'center', background: '#fff3cd' }}>
                <div style={{ fontWeight: 700, color: '#92400e', fontSize: 12 }}>Desconto À Vista</div>
                <div style={{ fontWeight: 700, fontSize: 16, marginTop: 2, color: '#92400e' }}>- R$ {num(totalDesconto)}</div>
              </td>
            )}
            <td style={{ border: '1px solid #1a3c6b', padding: '6px 10px', fontSize: 13, textAlign: 'center', background: '#1a3c6b' }}>
              <div style={{ fontWeight: 700, color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>Total Líquido</div>
              <div style={{ fontWeight: 700, fontSize: 18, marginTop: 2, color: '#fff' }}>R$ {num(totalLiquido)}</div>
            </td>
          </tr>
        </tbody>
      </table>
        )
      })()}

      {/* ── Assinatura ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 16, fontSize: 13 }}>
        <div>
          <div>{[organizador.cidade, organizador.estado].filter(Boolean).join(' - ')}, {fmtDate(leilao.data_leilao)}</div>
          <div style={{ color: '#1a3c6b', marginTop: 4 }}>Declaro que estou de acordo com as especificações acima.</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ borderTop: '1px solid #000', width: 220, margin: '0 auto 4px' }} />
          <div style={{ fontWeight: 700 }}>{vend.nome}</div>
          <div style={{ color: '#999', fontSize: 10 }}>Assinatura do Vendedor</div>
        </div>
      </div>
    </div>
  )
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function FaturaVendedorPreview({ dados, orgId, leilaoId, vendedorId, showMediasSexo = false, showMediaGeral = false, showDadosBancarios = false, ocultarParcelas = false, colunasAtivas = null, modoAnimais = null, separacao = null, isencaoDefesaAtiva = false, onSeparar, onReload }) {
  const iframeRef = useRef(null)
  const [pdfUrl,       setPdfUrl]       = useState(null)
  const [loadingPdf,   setLoadingPdf]   = useState(false)
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [showAlterarComissao, setShowAlterarComissao] = useState(false)
  const [showDespesa, setShowDespesa] = useState(false)
  const [showIsencao, setShowIsencao] = useState(null) // 'isentar' | 'desfazer' | null
  const [showAplicarIsencao, setShowAplicarIsencao] = useState(false)
  const [showSeparar, setShowSeparar] = useState(false)

  if (!dados) return null
  const { leilao, organizador, vendedores } = dados
  // Um único vendedor selecionado (mesmo que a fatura esteja separada em várias)
  const unico = vendedores.length === 1 || (!!dados.separacao_opcoes && vendedores.length > 0)
  // Lotes originais do vendedor = união das faturas separadas
  const lotesVendaAll = vendedores.flatMap(v => v.lotes_venda || [])
  const lotesDefesaAll = vendedores.flatMap(v => v.lotes_defesa || [])
  const showDb = showDadosBancarios || leilao?.fluxo_comissao === 'separada'

  const handleImprimir = async () => {
    setLoadingPdf(true)
    try {
      const qs = new URLSearchParams()
      if (vendedorId) qs.set('vendedor_id', vendedorId)
      if (vendedorId && separacao) qs.set('separacao', JSON.stringify(separacao))
      qs.set('mostrar_dados_bancarios', String(showDb))
      const resp = await api.get(
        `/organizadores/${orgId}/mapas/leilao/${leilaoId}/fatura-vendedor?${qs}`,
        { responseType: 'blob', timeout: 60000 }
      )
      const blob = new Blob([resp.data], { type: 'application/pdf' })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(blob)
      openPdf(url, setPdfUrl, setShowPdfModal)
    } catch (err) {
      alert('Erro ao gerar PDF: ' + (err.response?.statusText || err.message))
    } finally {
      setLoadingPdf(false)
    }
  }

  const closePdfModal = () => {
    setShowPdfModal(false)
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) }
  }

  const printPdf = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus()
      iframeRef.current.contentWindow.print()
    }
  }

  const nomeArquivo = (() => {
    const nomeVend = unico ? vendedores[0].nome : `Todos (${vendedores.length})`
    return `Fatura do Vendedor - ${nomeVend}.pdf`.replace(/[/\\?%*:|"<>]/g, '-')
  })()

  return (
    <>
      {/* ── Barra de ação ── */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <p className="text-sm text-gray-600 flex-1 min-w-0 truncate">
          <span className="font-medium text-gray-800">{leilao.nome}</span>
          {' · '}
          {unico ? vendedores[0].nome : `${vendedores.length} vendedores`}
          {separacao && vendedores.length > 1 && (
            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium align-middle">
              Separada em {vendedores.length} faturas
              <button onClick={() => onSeparar?.(null)} title="Voltar à fatura única" className="hover:text-red-600"><XIcon className="w-3 h-3" /></button>
            </span>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleImprimir}
            disabled={loadingPdf}
            className="flex items-center gap-1.5 px-4 py-2.5 sm:px-3 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-xs font-semibold rounded-lg transition-colors disabled:opacity-60"
          >
            <Printer className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">{loadingPdf ? 'Gerando...' : 'Imprimir / PDF'}</span>
            <span className="sm:hidden">{loadingPdf ? 'Gerando...' : 'PDF'}</span>
          </button>
          {dados.separacao_opcoes && onSeparar && (
            <button onClick={() => setShowSeparar(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg transition-colors"
              title="Separar a fatura por tipo de animal, sexo ou fazenda">
              <Split className="w-3.5 h-3.5" />
              Separar fatura
            </button>
          )}
          {unico && (
            <>
              <button onClick={async () => {
                try {
                  const resp = await api.get(
                    `/organizadores/${orgId}/recibos/mapa-vendedor/${leilaoId}/${vendedores[0].id}/recibo-pagamento`,
                    { responseType: 'blob', timeout: 30000 }
                  )
                  const blob = new Blob([resp.data], { type: 'application/pdf' })
                  if (pdfUrl) URL.revokeObjectURL(pdfUrl)
                  openPdf(URL.createObjectURL(blob), setPdfUrl, setShowPdfModal)
                } catch (err) {
                  import('react-hot-toast').then(m => m.default.error('Erro ao gerar recibo de pagamento'))
                }
              }}
                className="flex items-center gap-1 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded-lg transition-colors"
                title="Recibo de pagamento do valor líquido ao vendedor">
                Recibo Pagamento
              </button>
              {['separada', 'cavalo'].includes(leilao?.fluxo_comissao) && (
                <button onClick={async () => {
                  try {
                    const resp = await api.get(
                      `/organizadores/${orgId}/recibos/mapa-vendedor/${leilaoId}/${vendedores[0].id}/recibo-comissao`,
                      { responseType: 'blob', timeout: 30000 }
                    )
                    const blob = new Blob([resp.data], { type: 'application/pdf' })
                    if (pdfUrl) URL.revokeObjectURL(pdfUrl)
                    openPdf(URL.createObjectURL(blob), setPdfUrl, setShowPdfModal)
                  } catch (err) {
                    import('react-hot-toast').then(m => m.default.error('Erro ao gerar recibo de comissão'))
                  }
                }}
                  className="flex items-center gap-1 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium rounded-lg transition-colors"
                  title="Recibo de recebimento de comissão + despesas">
                  Recibo Comissão
                </button>
              )}
              <button
                onClick={() => setShowAlterarComissao(true)}
                className="hidden sm:flex items-center gap-1.5 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors"
              >
                Alterar Com.
              </button>
              <button onClick={() => setShowDespesa(true)}
                className="flex items-center gap-1 px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white text-xs font-medium rounded-lg transition-colors"
                title="Adicionar, remover ou restaurar despesas dos lotes do vendedor">
                Despesas
              </button>
              {/* Isentar/Desfazer Defesa */}
              {(() => {
                const lotesDefesa = lotesDefesaAll
                const temComissaoDefesa = lotesDefesa.some(l =>
                  l.cv_val > 0 && !l.defesa_isenta && (l.defesa_elegivel === null || l.defesa_elegivel === true)
                )
                const temIsento = lotesDefesa.some(l => l.defesa_isenta)
                const loteComPct = lotesDefesa.find(l => l.defesa_pct_defendido !== null && l.defesa_pct_defendido !== undefined)
                return (
                  <>
                    {isencaoDefesaAtiva && loteComPct && (
                      <span className="flex items-center px-2 py-2 text-sm font-semibold text-gray-800" title="% defendido (em valor) desse vendedor nesse leilão">
                        defendeu {loteComPct.defesa_pct_defendido.toFixed(2)}%
                      </span>
                    )}
                    {temComissaoDefesa && (
                      <button onClick={() => setShowIsencao('isentar')}
                        className="flex items-center gap-1 px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg transition-colors"
                        title="Zerar comissão de defesa">
                        Isentar Defesa
                      </button>
                    )}
                    {temIsento && (
                      <button onClick={() => setShowIsencao('desfazer')}
                        className="flex items-center gap-1 px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white text-xs font-medium rounded-lg transition-colors"
                        title="Restaurar comissão de defesa original">
                        Desfazer Isenção
                      </button>
                    )}
                    {isencaoDefesaAtiva && (
                      <button onClick={() => setShowAplicarIsencao(true)}
                        className="flex items-center gap-1 px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg transition-colors"
                        title="Sugerir e aplicar isenção de defesa pros vendedores elegíveis do leilão inteiro (não só o selecionado aqui)">
                        Aplicar Isenção (leilão)
                      </button>
                    )}
                  </>
                )
              })()}
            </>
          )}
        </div>
      </div>

      {/* ── Aviso mobile (oculto em sm+) ── */}
      <div className="sm:hidden bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
        <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p>O preview é otimizado para desktop.</p>
        <p className="mt-1">Use os botões acima para gerar o PDF.</p>
      </div>

      {/* ── Preview (oculto no mobile) ── */}
      <div className="hidden sm:block" style={{ background: '#f3f4f6', padding: '24px 0' }}>
        {vendedores.map((vend, i) => (
          <div key={`${vend.id || 'v'}-${i}`} style={{
            maxWidth: 1100, margin: '0 auto 24px', background: '#fff',
            boxShadow: '0 2px 12px rgba(0,0,0,0.10)', borderRadius: 8, overflow: 'hidden',
          }}>
            <FaturaVendedorPage vend={vend} leilao={leilao} organizador={organizador} showMediasSexo={showMediasSexo} showMediaGeral={showMediaGeral} showDadosBancarios={showDadosBancarios} ocultarParcelas={ocultarParcelas} colunasAtivas={colunasAtivas} modoAnimais={modoAnimais} />
          </div>
        ))}
      </div>

      {/* ── Modal PDF ── */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white w-full max-w-5xl flex flex-col rounded-t-2xl sm:rounded-xl shadow-2xl" style={{ height: '92dvh' }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sm:rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800 text-sm truncate flex-1 min-w-0">Fatura do Vendedor — {leilao.nome}</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => downloadPdf(pdfUrl, nomeArquivo)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Salvar PDF
                </button>
                <button onClick={printPdf}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Printer className="w-4 h-4" />
                  Imprimir
                </button>
                <button onClick={closePdfModal}
                  className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">
                  Fechar
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title={nomeArquivo}
                style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}

      {/* Modal Alterar Comissão */}
      {showAlterarComissao && unico && (
        <AlterarComissaoModal
          tipo="venda"
          lotes={lotesVendaAll.filter(l => l.id)}
          organizadorId={orgId}
          onClose={() => setShowAlterarComissao(false)}
          onSaved={() => { if (onReload) onReload() }}
        />
      )}

      {/* Modal Despesas (adicionar / remover / restaurar) */}
      {showDespesa && unico && (
        <DespesasModal
          lotes={[lotesVendaAll, lotesDefesaAll, vendedores.flatMap(v => v.lotes_nao_movimentados || [])]
            .map(arr => arr.filter(l => l.id))
            .find(arr => arr.length > 0) || []}
          organizadorId={orgId}
          onClose={() => setShowDespesa(false)}
          onSaved={() => { if (onReload) onReload() }}
        />
      )}

      {/* Modal Separar Fatura */}
      {showSeparar && dados.separacao_opcoes && (
        <SepararFaturaModal
          opcoes={dados.separacao_opcoes}
          fluxo={leilao?.fluxo_comissao}
          atual={separacao}
          onClose={() => setShowSeparar(false)}
          onApply={(cfg) => { setShowSeparar(false); onSeparar?.(cfg) }}
        />
      )}

      {/* Modal Isentar/Desfazer Defesa */}
      {showIsencao && unico && (
        <IsentarDefesaModal
          modo={showIsencao}
          lotesDefesa={lotesDefesaAll}
          organizadorId={orgId}
          onClose={() => setShowIsencao(null)}
          onSaved={() => { setShowIsencao(null); if (onReload) onReload() }}
        />
      )}

      {/* Modal Aplicar Isenção (sugestão em lote pro leilão inteiro) */}
      {showAplicarIsencao && (
        <AplicarIsencaoModal
          organizadorId={orgId}
          leilaoId={leilaoId}
          onClose={() => setShowAplicarIsencao(false)}
          onSaved={() => { setShowAplicarIsencao(false); if (onReload) onReload() }}
        />
      )}
    </>
  )
}
